/**
 * Cross-cutting dimensions tagged onto upload-funnel telemetry so dashboards
 * can split completion rate / resume-success / re-pick-required by network
 * class and browser family.
 *
 * Safe on Safari (where `navigator.connection` is undefined) — falls back to
 * 'unknown' for the effective type.
 */

interface NetworkInformationLike {
  effectiveType?: string;
  saveData?: boolean;
  downlink?: number;
}

const getConnection = (): NetworkInformationLike | undefined => {
  if (typeof navigator === 'undefined') return undefined;
  return (navigator as Navigator & { connection?: NetworkInformationLike }).connection;
};

const detectUaFamily = (): 'ios_safari' | 'ios_chrome' | 'android_chrome' | 'desktop_safari' | 'desktop_chrome' | 'other' => {
  if (typeof navigator === 'undefined') return 'other';
  const ua = navigator.userAgent;
  // iOS first — iOS Chrome uses WebKit and identifies as CriOS/FxiOS but
  // also includes iPhone/iPad in UA. Check iOS markers first.
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !(window as { MSStream?: unknown }).MSStream;
  if (isIOS) {
    if (/CriOS\//.test(ua)) return 'ios_chrome';
    return 'ios_safari';
  }
  if (/Android/.test(ua) && /Chrome\//.test(ua)) return 'android_chrome';
  if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) return 'desktop_safari';
  if (/Chrome\//.test(ua)) return 'desktop_chrome';
  return 'other';
};

export interface UploadDimensions {
  effectiveType: string;
  uaFamily: string;
  saveData: boolean;
}

export const getUploadDimensions = (): UploadDimensions => {
  const conn = getConnection();
  return {
    effectiveType: conn?.effectiveType ?? 'unknown',
    uaFamily: detectUaFamily(),
    saveData: conn?.saveData ?? false,
  };
};
